import {Address, GooglePlacesAutocompleteAddress} from './Address';
import {Contact, ProviderFullContact} from './Contacts';
import {LegalDetails} from './LegalDetails';
import {ProviderPaymentMethod} from './PaymentMethod';

export type Provider = {
  id: number;
  uid: string;
  name: string;
  has_stock_share_integration: boolean;
  has_contract: boolean;
  is_active: boolean;
  created_at: string | Date;
  updated_at: string | Date;
  legal_details_id: number;
  contract_type: string;
  provider_product_type: Array<{
    uid: string;
    provider_id: number;
    product_type_id: number;
  }>;
  provider_purchase_type: Array<{
    uid: string;
    provider_id: number;
    purchase_type_id: number;
  }>;
  offer_integration_update_recurrence?: number;
  offer_integration_update_origin_email?: string;
  offer_integration_type: string;
  autopart_provider_type: string;
  kind: string;
  delivery_to_mecanizou?: boolean;
  provider_stocks: ProviderStock[];
  provider_purchase_infos: ProviderPurchaseInfos;
  provider_warranty_infos: ProviderWarrantyInfos;
  provider_payment_methods: ProviderPaymentMethod[];
  provider_autopart_categories: any[];
  legal_details: LegalDetails;
  provider_contacts?: ProviderFullContact[];
};

export type ProviderStock = {
  id?: number;
  uid: string;
  provider_id: number;
  address_id: number;
  name: string;
  cnpj: string;
  full_address: GooglePlacesAutocompleteAddress;
  address: Address;
  delivery_to_mecanizou: boolean;
  has_offer_integration: boolean;
  has_purchase_integration: boolean;
  is_generalist_provider_stock: boolean;
  expedition_time_minutes: number;
  legal_details_id?: number;
  provider_stock_opening_hours: OpeningHour[];
  dispatch_opening_hours: OpeningHour[];
  provider_stock_incident_infos: ProviderStockIncidentInfos;
  provider_stock_finance_infos: ProviderStockFinanceInfos;
  legal_details: LegalDetails;
  provider_purchase_infos: ProviderPurchaseInfos[];
  provider_autopart_categories: [];
  provider_vehicle_brands: [];
  provider_stock_contacts: ProviderFullContact[];
  is_active?: boolean;
  created_at?: string | Date;
  updated_at?: string | Date;
  contact_reference?: string;
  provider_stock_account_uid?: string | null;
};

export interface ProviderStockInfo {
  uid: string;
  provider_id: number;
  address_id: number;
  name?: string;
  cnpj: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  delivery_to_mecanizou: boolean;
  is_generalist_provider_stock: boolean;
  legal_details_id: any;
  address: Address;
  provider: Provider;
  legal_details: any;
  distance: number;
  contact_reference: string;
}

export type NewProviderModel = {
  name: string;
  contract_type: string;
  autopart_provider_type: string;
  provider_product_type: number[];
  provider_purchase_type: number[];
  offer_integration_type: string;
  offer_integration_update_origin_email?: string;
  offer_integration_update_recurrence?: number;
  provider_stocks?: ProviderStock[];
  warehouses?: ProviderStock[];
  provider_contacts: Contact[];
  provider_purchase_infos?: ProviderPurchaseInfos;
  provider_warranty_infos: ProviderWarrantyInfos;
};

export type ProviderAddress = {
  address: string;
  city: string;
  state: string;
  address_number: string;
  address_complement: string;
  zipcode: string;
  neighborhood: string;
  lat: string | number;
  long: string | number;
};

export type OpeningHour = {
  id?: number;
  uid?: string;
  type: 'sales' | 'dispatch';
  init_weekday: string;
  end_weekday: string;
  opening_hour: string;
  closing_hour: string;
  lunch_break_start_hour?: string;
  lunch_break_end_hour?: string;
  provider_stock_id?: number;
  comments?: string;
  created_at?: string | Date;
  updated_at?: string | Date;
  is_active?: boolean;
};

export type ProviderPurchaseInfos = {
  id?: number;
  uid?: string;
  provider_id?: number;
  is_active?: boolean;
  purchase_format?: string;
  website?: string;
  login?: string;
  password?: string;
  purchase_manual?: string;
  whatsapp_group?: string;
  created_at?: string | Date;
  updated_at?: string | Date;
};

export type ProviderWarrantyInfos = {
  id?: number;
  uid?: string;
  provider_id?: number;
  is_active?: boolean;
  days_to_request?: number;
  warranty_process?: string;
  contacts?: Contact[];
  created_at?: string | Date;
  updated_at?: string | Date;
};

export type ProviderStockIncidentInfos = {
  id?: number;
  uid?: string;
  is_active?: boolean;
  provider_stock_id?: number;
  incident_process: string;
  return_period: number;
  comments?: string;
  contacts: Contact[];
  created_at?: string | Date;
  updated_at?: string | Date;
};

export type ProviderStockFinanceInfos = {
  id?: number;
  uid?: string;
  is_active?: boolean;
  provider_stock_id?: number;
  is_billing_enabled: boolean;
  credit_limit?: string;
  days_to_payment?: string;
  cycle_days?: number;
  min_purchase_amount?: string;
  is_credit_card_enabled: boolean;
  discount_percentage?: number | string;
  contacts: Contact[];
  created_at?: string | Date;
  updated_at?: string | Date;
};

export type GetProvidersRequestData = {
  kind?: string;
  sortName?: string;
  name?: string;
  tier?: string;
  items_per_page?: number;
  page?: number;
};

export type GetIntegratedPurchaseProvidersResponseData = {
  integratedPurchaseProviders: IntegratedPurchaseProvider[];
};

export type GetIntegratedLogisticsProvidersResponseData = {
  integratedLogisticsProviders: IntegratedLogisticsProvider[];
};

/** Uma linha por filial (`warehouse_uid`) com compra automática. */
export type IntegratedPurchaseProvider = {
  id: number;
  uid: string;
  is_active: boolean;
  created_at: string | Date;
  updated_at: string | Date;
  provider_uid: string;
  provider_name: string;
  warehouse_uid: string;
  warehouse_name: string;
  is_online: boolean;
};

export type IntegratedLogisticsProvider = {
  id: number;
  uid: string;
  is_active: boolean;
  created_at: string | Date;
  updated_at: string | Date;
  logistics_provider_uid: string;
  logistics_provider_name: string;
  is_online: boolean;
};

export type SelectedProviderInfosState = {
  id: number;
  uid: string;
  name: string;
  provider_stocks: Array<ProviderStock>;
  provider_contacts: ProviderFullContact[];
  provider_purchase_infos: ProviderPurchaseInfos[];
};

export type SelectedProviderContextProps = {
  state: SelectedProviderInfosState;
  updateState: (updates: SelectedProviderInfosState) => void;
};

/**
 * Motivos de desativacao oferecidos no painel.
 *
 * O crm-service guarda `reason` como texto livre, entao o que viaja no payload
 * e o rotulo em portugues (ou o texto digitado em "Outro") -- e isso que
 * aparece na auditoria e em qualquer relatorio depois. Manter o catalogo aqui
 * evita migration a cada motivo novo.
 */
export const providerStatusChangeReasonsDictionary = {
  out_of_stock: 'Sem estoque',
  incorrect_price: 'Preco incorreto',
  provider_issue: 'Problema com fornecedor',
  inconsistent_offer: 'Oferta inconsistente',
  commercial_block: 'Bloqueio comercial',
  other: 'Outro',
};
